import { StrictMode, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';

import AppProviders from './AppProviders';
import { handleRedirectAuthEvent } from './authRedirectState';
import { msalInstance } from '@/config/msalConfig';
import { hasAuthTokens } from '@/lib/authToken';

/** Basename for the current page, not for the build.
 *
 *  A portal built with `--base /nova/` is reachable both under that prefix
 *  (orr-dev.pegasus.wkcloud.io/nova, where the gateway strips it before the
 *  Static Web App sees it) and at the root of its own hostname
 *  (nova-dev.pegasus.wkcloud.io). Only the live path says which one applies.
 *
 *  Portals built at the root have an empty prefix and always get '/'. */
const resolveBasename = (): string => {
  const prefix = import.meta.env.BASE_URL.replace(/\/+$/, '');
  if (!prefix) return '/';
  const { pathname } = window.location;
  const underPrefix =
    pathname === prefix || pathname.startsWith(`${prefix}/`);
  return underPrefix ? prefix : '/';
};

export const bootstrapPortal = (portal: ReactElement) => {
  const isAuthTokenMode = hasAuthTokens();

  if (isAuthTokenMode) {
    const fakeAccount = {
      homeAccountId: 'auth-token.account',
      environment: 'login.microsoftonline.com',
      tenantId: '8ac76c91-e7f1-41ff-a89c-3553b2da2c17',
      username: 'auth-token@local',
      localAccountId: 'auth-token.local',
      name: 'Auth Token User',
    };
    msalInstance.getActiveAccount = () => fakeAccount;
    msalInstance.getAllAccounts = () => [fakeAccount];
  }

  msalInstance.initialize().then(async () => {
    // Default to using the first account if no account is active on page load
    if (
      !isAuthTokenMode &&
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      const account = msalInstance.getAllAccounts()[0];

      if (account) {
        msalInstance.setActiveAccount(account);
      }
    }

    // Optional - This will update account state if a user signs in from another tab or window
    msalInstance.enableAccountStorageEvents();

    // Capture redirect failures before MsalProvider processes the callback.
    msalInstance.addEventCallback((event) => {
      handleRedirectAuthEvent(event);

      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const payload = event.payload as AuthenticationResult;
        if (payload.account) {
          msalInstance.setActiveAccount(payload.account);
        }
      }
    });

    const { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } =
      await import('ag-grid-community');

    ModuleRegistry.registerModules([AllCommunityModule]);
    provideGlobalGridOptions({ theme: 'legacy' });

    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        {/* NOVA is served under /nova/ behind the App Gateway (built with
            --base /nova/); the other portals sit at the root. BASE_URL carries
            whichever applies, so portal routes stay path-relative either way.
            The trailing slash has to go: React Router compares the basename
            against the raw path, so "/nova/" refuses to match "/nova" and
            renders nothing at all.

            BASE_URL alone is not enough: the same NOVA bundle also answers on
            nova-dev.pegasus.wkcloud.io, where the path has no /nova prefix.
            A build-time "/nova" basename cannot match "/" there, and the
            Router renders nothing. Decide from the live path instead. */}
        <BrowserRouter basename={resolveBasename()}>
          <AppProviders msalInstance={msalInstance}>{portal}</AppProviders>
        </BrowserRouter>
      </StrictMode>,
    );
  });
};
